import React, { memo, useEffect, useState } from 'react';

import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import { useDataProvider, useRefresh, useNotify, useVersion } from 'react-admin';

import { Chip, makeStyles } from '@material-ui/core';

import { FraudDetailsSection } from './FraudDetailsSection';
import EnableUserButton from './EnableUserButton';
import ChangeEmailButton from './ChangeEmailButton';
import UserStatusField from './UserStatusField';
import ResetPasswordButton from './ResetPasswordButton';
import VerificationStatus from './VerificationStatus';
import BypassFraudButton from './BypassFraudButton';
import BlockShippingButton from './BlockShippingButton';
import SuspendAsFraudButton from './SuspendAsFraudButton';
import SpendingLimitsSection from './SpendingLimitsSection';
import BlockReasonSection from './BlockReasonSection';
import CardChargingBlockButton from './CardChargingBlockButton';
import SmallPackagesAllowButton from './SmallPackagesAllowButton';
import IdentityVerification from './IdentityVerification';
import ReleaseAchHoldButton from './ReleaseAchHoldButton';
import CardPaymentAllowButton from './CardPaymentAllowButton';

const useStyles = makeStyles((theme) => ({
  managementCard: {
    padding: theme.spacing(3),
  },

  statusContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  label: {
    fontSize: 16,
    color: theme.palette.secondary.contrastText,
    fontWeight: 700,
  },

  button: {
    color: theme.palette.secondary.contrastText,
    textTransform: 'none',
    minWidth: '110px',
  },

  action: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },

  userSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
  },

  divider: {
    padding: 0,
  },

  container: {
    marginTop: theme.spacing(2),
  },

  verificationContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: '16px',
    gap: '16px',
  },
  verificationStatuses: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
  },
  authorizationContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '16px',
  },
  tiersContainer: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  tierChip: {
    cursor: 'pointer',
  },
}));

const ManagementTab = ({ user }) => {
  const {
    id: rolloId,
    cognitoUserId: cognitoId,
    fraudAllowedAt: bypassFraud,
    phoneVerified,
    shippingBlocked,
    emailVerified,
    fraudDetectedAt,
    chargingBlockedAt,
    smallPackagesAllowedAt,
    billingUserId,
    currency,
  } = user;

  const classes = useStyles();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const notify = useNotify();
  const version = useVersion();

  const [loading, setLoading] = useState<boolean>(false);
  const [billingSettings, setBillingSettings] = useState<any>(null);
  const [achTiers, setAchTiers] = useState<number[]>([]);

  useEffect(() => {
    const getBillingSettings = async () => {
      try {
        const {
          data: { billingUserSettings },
        } = await dataProvider.getNest('userDetails', {
          userId: rolloId,
          billingUserId,
          currency,
        });

        setBillingSettings(billingUserSettings);
      } catch (error: any) {
        console.error(error?.message ?? error);
      }
    };

    getBillingSettings();
  }, [billingUserId, currency, dataProvider, rolloId, version]);

  useEffect(() => {
    const getAchTiers = async () => {
      try {
        const { data } = await dataProvider.getList('achTiers');

        setAchTiers(data?.map((item: any) => item.tier));
      } catch (error: any) {
        console.error(error?.message ?? error);
      }
    };

    getAchTiers();
  }, [dataProvider]);

  const handleVerifyEmail = async () => {
    try {
      setLoading(true);

      await dataProvider.post('confirmUser', { data: { rolloId, cognitoId } });

      refresh();
    } catch (error: any) {
      notify(
        typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
        'warning'
      );
    } finally {
      setLoading(false);
    }
  };

  const getVerificationLabel = () => {
    if (!phoneVerified && !emailVerified) {
      return 'Verify Email & Phone';
    }

    if (phoneVerified && !emailVerified) {
      return 'Verify Email';
    }

    if (!phoneVerified && emailVerified) {
      return 'Verify Phone';
    }
  };

  const handleResetAuthorization = async () => {
    try {
      setLoading(true);

      await dataProvider.post('resetAuthorization', { data: { rolloId, cognitoId } });

      refresh();
    } catch (error: any) {
      notify(
        typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
        'warning'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTierClick = async (tier: number) => {
    try {
      setLoading(true);
      await dataProvider.update('updateUserRiskTier', {
        data: {
          userId: billingUserId,
          riskTier: tier,
        },
      });

      refresh();
    } catch (error: any) {
      notify(
        typeof error === 'string' ? error : error.message || 'ra.notification.http_error',
        'warning'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={2} className={classes.container}>
      <Grid item md={6} xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card className={classes.managementCard}>
              <Box className={classes.verificationContainer}>
                <Box className={classes.verificationStatuses}>
                  <Box className={classes.statusContainer}>
                    <Typography className={classes.label}>Email address</Typography>

                    <VerificationStatus
                      verified={emailVerified}
                      label={emailVerified ? 'Verified' : 'Unverified'}
                    />

                    <Box className={classes.userSection}>
                      <ChangeEmailButton user={user} />
                    </Box>
                  </Box>

                  <Divider variant="middle" />

                  <Box className={classes.statusContainer}>
                    <Typography className={classes.label}>Phone number</Typography>

                    <VerificationStatus
                      verified={phoneVerified}
                      label={phoneVerified ? 'Verified' : 'Unverified'}
                    />
                    {(!phoneVerified || !emailVerified) && (
                      <Button
                        variant="outlined"
                        className={classes.button}
                        onClick={handleVerifyEmail}
                        disabled={loading}>
                        {getVerificationLabel()}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Box>

              <Divider variant="middle" />

              <Box className={classes.authorizationContainer}>
                <Typography className={classes.label}>Authorization settings</Typography>

                <div>
                  <Button
                    variant="outlined"
                    onClick={handleResetAuthorization}
                    className={classes.button}
                    disabled={loading}>
                    Reset to email
                  </Button>
                </div>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <IdentityVerification user={user} />
          </Grid>
        </Grid>
      </Grid>

      <Grid item md={6} xs={12}>
        <Card className={classes.managementCard}>
          <Grid container spacing={4}>
            <Grid xs={12} item container alignItems="center">
              <Grid item xs={3}>
                <Typography className={classes.label}>User status</Typography>
              </Grid>

              <Grid item xs={3}>
                <UserStatusField record={user} />
              </Grid>

              <Grid item xs={6} className={classes.action}>
                <Box className={classes.userSection}>
                  <EnableUserButton user={user} />
                  <ResetPasswordButton user={user} />
                </Box>
              </Grid>
            </Grid>

            <Grid item xs={12} className={classes.divider}>
              <Divider variant="middle" />
            </Grid>

            <Grid item container xs={12} alignItems="center">
              <Grid item xs={3}>
                <Typography className={classes.label}>Fraud monitoring</Typography>
              </Grid>

              <Grid item xs={3}>
                <VerificationStatus
                  verified={!fraudDetectedAt}
                  label={Boolean(fraudDetectedAt) ? 'Suspended' : 'Unsuspended'}
                />
              </Grid>

              <Grid item xs={6} className={classes.action}>
                <Box className={classes.userSection}>
                  <SuspendAsFraudButton user={user} />
                </Box>
              </Grid>
            </Grid>

            <Grid item container xs={12} alignItems="center">
              <Grid item xs={3}>
                <Typography className={classes.label}></Typography>
              </Grid>

              <Grid item xs={3}>
                <VerificationStatus
                  verified={!bypassFraud}
                  label={Boolean(bypassFraud) ? 'Disabled' : 'Enabled'}
                />
              </Grid>

              <Grid item xs={6} className={classes.action}>
                <Box className={classes.userSection}>
                  <BypassFraudButton user={user} />
                </Box>
              </Grid>
            </Grid>

            <Grid item xs={12} className={classes.divider}>
              <Divider variant="middle" />
            </Grid>

            <Grid item container xs={12} alignItems="center">
              <Grid item xs={3}>
                <Typography className={classes.label}>Shipping</Typography>
              </Grid>

              <Grid item xs={3}>
                <VerificationStatus
                  verified={!shippingBlocked}
                  label={Boolean(shippingBlocked) ? 'Disabled' : 'Enabled'}
                />
              </Grid>

              <Grid item xs={6} className={classes.action}>
                <BlockShippingButton user={user} />
              </Grid>
            </Grid>

            <Grid item xs={12} className={classes.divider}>
              <Divider variant="middle" />
            </Grid>

            <Grid item container xs={12} alignItems="center">
              <Grid item xs={3}>
                <Typography className={classes.label}>Card charging</Typography>
              </Grid>

              <Grid item xs={3}>
                <VerificationStatus
                  verified={!chargingBlockedAt}
                  label={Boolean(chargingBlockedAt) ? 'Disabled' : 'Enabled'}
                />
              </Grid>

              <Grid item xs={6} className={classes.action}>
                <CardChargingBlockButton user={user} />
              </Grid>
            </Grid>

            <Grid item container xs={12} alignItems="center">
              <Grid item xs={3}>
                <Typography className={classes.label}>Hide alerts on small packages</Typography>
              </Grid>

              <Grid item xs={3}>
                <VerificationStatus
                  verified={!!smallPackagesAllowedAt}
                  label={!smallPackagesAllowedAt ? 'Disabled' : 'Enabled'}
                />
              </Grid>

              <Grid item xs={6} className={classes.action}>
                <SmallPackagesAllowButton user={user} />
              </Grid>
            </Grid>

            <Grid item xs={12} className={classes.divider}>
              <Divider variant="middle" />
            </Grid>

            <Grid item container xs={12} alignItems="center">
              <Grid item xs={3}>
                <Typography className={classes.label}>ACH security tier</Typography>
              </Grid>

              <Grid item xs={4}>
                <Box className={classes.tiersContainer}>
                  {achTiers?.map((tier) => {
                    const isActive = billingSettings?.achRiskTier === tier;

                    return (
                      <Chip
                        onClick={loading || isActive ? () => {} : () => handleTierClick(tier)}
                        className={classes.tierChip}
                        key={tier}
                        label={tier}
                        color={isActive ? 'primary' : 'default'}
                      />
                    );
                  })}
                </Box>
              </Grid>

              <Grid item xs={5} className={classes.action}>
                <ReleaseAchHoldButton userId={user.id} achRiskTier={billingSettings?.achRiskTier} />
              </Grid>
            </Grid>

            <Grid item container xs={12} alignItems="center">
              <Grid item xs={3}>
                <Typography className={classes.label}>Card payments</Typography>
              </Grid>

              <Grid item xs={3}>
                <VerificationStatus
                  verified={!!billingSettings?.creditCardPaymentAllowed}
                  label={!billingSettings?.creditCardPaymentAllowed ? 'Disabled' : 'Enabled'}
                />
              </Grid>

              <Grid item xs={6} className={classes.action}>
                <CardPaymentAllowButton
                  user={user}
                  creditCardPaymentAllowed={billingSettings?.creditCardPaymentAllowed}
                />
              </Grid>
            </Grid>
          </Grid>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <SpendingLimitsSection userId={rolloId} />
      </Grid>

      <Grid item xs={12} md={6}>
        <BlockReasonSection user={user} />
      </Grid>

      <Grid item xs={12}>
        <FraudDetailsSection user={user} />
      </Grid>
    </Grid>
  );
};

export default memo(ManagementTab);
